import React, { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { getAdvances, addAdvance, getSupervisors } from '../../../../../../services/apiaryService';
import close from '../../../assets/images/close.png';
import * as Styles from '../../../Farmers/components/FarmerForm/style';
import { Form, Row, Col, Button } from 'react-bootstrap';
import LoadingOverlay from 'react-loading-overlay';
import BounceLoader from 'react-spinners/BounceLoader';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { toast } from 'react-toastify';
import GenericExportCSVModal from '../../../shared/GenericExportCSVModal';

// Styled Components
const MainContainer = styled.div`
    padding: 20px 0;
    .modern-table {
        background: #fff;
        border-radius: 16px;
        box-shadow: 0 10px 30px rgba(0,0,0,0.05);
        overflow: hidden;
        margin-bottom: 20px;
        border: 1px solid #f0f0f0;
    }
    .modern-table thead th {
        border-bottom: 2px solid #f1f2f6 !important;
        background: #fafafa;
        color: #636e72;
        font-weight: 700;
        font-size: 13px;
        padding: 18px 15px;
        text-transform: uppercase;
        letter-spacing: 1px;
    }
    .add-advance-btn {
        background: linear-gradient(135deg, #00b894 0%, #55efc4 100%);
        color: white;
        border: none;
        padding: 12px 24px;
        border-radius: 12px;
        font-weight: 700;
        display: flex;
        align-items: center;
        gap: 10px;
        box-shadow: 0 4px 15px rgba(0, 184, 148, 0.2);
        cursor: pointer;
    }
`;

const HeaderSection = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 24px;
`;

const TotalBadge = styled.div`
    display: flex;
    align-items: center;
    background: linear-gradient(135deg, #2d3436 0%, #000000 100%);
    padding: 12px 24px;
    border-radius: 16px;
    color: #fff;
    box-shadow: 0 8px 20px rgba(0,0,0,0.15);
`;

const AddAdvanceModal = ({ show, onClose, apiaryId, onSuccess }) => {
    const [formData, setFormData] = useState({
        issued_to: '',
        amount: '',
        date: new Date().toISOString().split('T')[0],
        type: 'ADVANCE', // ADVANCE or REIMBURSEMENT
        comments: ''
    });
    const [supervisors, setSupervisors] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        if (show) {
            getSupervisors().then(setSupervisors);
        }
    }, [show]);

    if (!show) return null;

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.issued_to) {
            toast.error("Please select a supervisor!");
            return;
        }
        setIsSubmitting(true);
        try {
            await addAdvance({ ...formData, apiary_id: apiaryId, amount: Number(formData.amount) });
            toast.success("Entry added successfully!");
            onSuccess();
            onClose();
        } catch (error) {
            toast.error(error.response?.data?.error || "Failed to add entry");
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Styles.ModalWrapper style={{ zIndex: 1050 }}>
            <Styles.ModalContent2 style={{ maxWidth: '500px' }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "20px" }}>
                    <h3 style={{ color: '#00b894', margin: 0 }}>Issue Advance / Reimbursement</h3>
                    <img src={close} alt="Close" onClick={onClose} style={{ width: 20, cursor: "pointer", opacity: 0.6 }} />
                </div>
                <Form onSubmit={handleSubmit} style={{ padding: '0 20px 20px 20px' }}>
                    <Form.Group className="mb-3">
                        <Form.Label>Type</Form.Label>
                        <Form.Control as="select" value={formData.type} onChange={e => setFormData({...formData, type: e.target.value})}>
                            <option value="ADVANCE">Advance</option>
                            <option value="REIMBURSEMENT">Reimbursement</option>
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Issued To</Form.Label>
                        <Form.Control 
                            as="select" 
                            value={formData.issued_to} 
                            onChange={e => setFormData({...formData, issued_to: e.target.value})}
                            required
                        >
                            <option value="">-- Select Personnel --</option>
                            {supervisors.map(s => (
                                <option key={s.id} value={s.id}>{s.name} ({s.role})</option>
                            ))}
                        </Form.Control>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Amount (₹)</Form.Label>
                        <Form.Control type="number" placeholder="Enter amount" value={formData.amount} onChange={e => setFormData({...formData, amount: e.target.value})} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Date</Form.Label>
                        <Form.Control type="date" value={formData.date} onChange={e => setFormData({...formData, date: e.target.value})} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Comments / UTR No.</Form.Label>
                        <Form.Control as="textarea" rows={2} value={formData.comments} onChange={e => setFormData({...formData, comments: e.target.value})} />
                    </Form.Group>
                    <div className="text-right">
                        <Button variant="light" onClick={onClose} className="mr-2">Cancel</Button>
                        <Button variant="success" type="submit" disabled={isSubmitting} style={{ background: '#00b894', border: 'none' }}>
                            {isSubmitting ? 'Saving...' : 'Save Entry'}
                        </Button>
                    </div>
                </Form>
            </Styles.ModalContent2>
        </Styles.ModalWrapper>
    );
};

const NectarAdvances = ({ apiaryId, onNavigateToExpenses }) => {
    const [advances, setAdvances] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showExportModal, setShowExportModal] = useState(false);

    const fetchAdvances = async () => {
        setLoading(true);
        try {
            const data = await getAdvances(apiaryId);
            setAdvances(data);
        } catch (err) {
            console.error(err);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (apiaryId) fetchAdvances();
    }, [apiaryId]);

    const columns = [
        {
            dataField: 'advance_id_display',
            text: 'ID',
            formatter: (cell, row) => <span style={{ fontWeight: 700, color: '#2d3436' }}>{cell || row._id.substring(0, 8).toUpperCase()}</span>
        },
        {
            dataField: 'date',
            text: 'Date',
            formatter: (cell) => new Date(cell).toLocaleDateString()
        },
        {
            dataField: 'type',
            text: 'Type',
            formatter: (cell) => (
                <span style={{ 
                    background: cell === 'REIMBURSEMENT' ? '#fff9db' : '#e6fffa', 
                    color: cell === 'REIMBURSEMENT' ? '#f08c00' : '#087f5b',
                    padding: '4px 10px', 
                    borderRadius: '200px',
                    fontSize: '11px',
                    fontWeight: 800
                }}>
                    {cell}
                </span>
            )
        },
        {
            dataField: 'issued_to_name',
            text: 'Issued To',
            formatter: (cell) => <span style={{ background: '#f1f2f6', padding: '4px 8px', borderRadius: '4px' }}>{cell || 'Staff'}</span>
        },
        {
            dataField: 'amount',
            text: 'Total Amount',
            formatter: (cell) => (
                <span 
                    style={{ fontWeight: 800, color: '#0984e3', textDecoration: 'underline', cursor: 'pointer' }} 
                    title="Click to view allocations"
                    onClick={() => onNavigateToExpenses && onNavigateToExpenses()}
                >
                    ₹ {cell.toLocaleString()}
                </span>
            )
        },
        {
            dataField: 'amount_left',
            text: 'Amount Left',
            formatter: (cell) => <span style={{ fontWeight: 800, color: cell > 0 ? '#00b894' : '#eb4d4b' }}>₹ {cell.toLocaleString()}</span>
        },
        {
            dataField: 'comments',
            text: 'Comments'
        }
    ];

    const EXPORT_COLUMNS = [
        { label: 'ID', key: 'id', formatter: (row) => row.advance_id_display || row._id?.substring(0, 8).toUpperCase() },
        { label: 'Date', key: 'date', formatter: (row) => row.date ? new Date(row.date).toLocaleDateString() : '' },
        { label: 'Type', key: 'type', path: 'type' },
        { label: 'Issued To', key: 'issued_to_name', formatter: (row) => row.issued_to_name || 'Staff' },
        { label: 'Total Amount', key: 'amount', path: 'amount' },
        { label: 'Amount Left', key: 'amount_left', path: 'amount_left' },
        { label: 'Comments', key: 'comments', path: 'comments' }
    ];

    return (
        <MainContainer>
            <HeaderSection>
                <TotalBadge>
                    <div style={{ marginRight: '15px' }}>
                        <i className="fa fa-university" style={{ fontSize: '24px', color: '#55efc4' }}></i>
                    </div>
                    <div>
                        <div style={{ fontSize: '11px', opacity: 0.7 }}>Active Advances</div>
                        <div style={{ fontSize: '20px', fontWeight: 800 }}>{advances.length}</div>
                    </div>
                </TotalBadge>
                <div style={{ display: 'flex', gap: '10px' }}>
                    <Button
                        variant="outline-secondary"
                        onClick={() => setShowExportModal(true)}
                        style={{ borderRadius: '12px', fontWeight: 700, padding: '10px 20px' }}
                    >
                        <i className="fa fa-download mr-2"></i> Export CSV
                    </Button>
                    <button className="add-advance-btn" onClick={() => setShowAddModal(true)}>
                        <i className="fa fa-plus"></i> Add Advance
                    </button>
                </div>
            </HeaderSection>

            <LoadingOverlay active={loading} spinner={<BounceLoader color={'#00b894'} />}>
                <BootstrapTable
                    keyField="_id"
                    data={advances}
                    columns={columns}
                    bordered={false}
                    className="modern-table"
                    pagination={paginationFactory({ sizePerPage: 10, hideSizePerPage: true })}
                />
            </LoadingOverlay>

            <AddAdvanceModal
                show={showAddModal}
                onClose={() => setShowAddModal(false)}
                apiaryId={apiaryId}
                onSuccess={fetchAdvances}
            />

            <GenericExportCSVModal
                show={showExportModal}
                onHide={() => setShowExportModal(false)}
                data={advances}
                columns={EXPORT_COLUMNS}
                title="Export Advances to CSV"
                filenamePrefix="apiary_advances_export"
            />
        </MainContainer>
    );
};

export default NectarAdvances;
